import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './DonutChartPage.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const DonutChartPage = () => {
  const [submissionsData, setSubmissionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubmissionsData();
  }, []);

  const fetchSubmissionsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/chart/submissions`);
      if (!response.ok) {
        throw new Error('Failed to fetch submissions data');
      }
      const data = await response.json();
      setSubmissionsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = {
    labels: submissionsData.map(item => item.status === 'Yes' ? 'Submitted' : 'Not Submitted'),
    datasets: [
      {
        data: submissionsData.map(item => item.count),
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(240, 147, 251, 0.8)',
        ],
        borderColor: [
          '#667eea',
          '#f093fb',
        ],
        borderWidth: 3,
        hoverBackgroundColor: [
          'rgba(102, 126, 234, 0.9)',
          'rgba(240, 147, 251, 0.9)',
        ],
        hoverBorderColor: [
          '#667eea',
          '#f093fb',
        ],
        hoverBorderWidth: 4,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568',
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      title: {
        display: true,
        text: 'Project Submissions Status',
        font: {
          size: 24,
          weight: 'bold',
          family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        },
        color: '#2d3748',
        padding: {
          top: 20,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#2d3748',
        bodyColor: '#4a5568',
        borderColor: '#667eea',
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    elements: {
      arc: {
        borderWidth: 3,
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
      easing: 'easeInOutQuart'
    }
  };

  if (loading) {
    return (
      <div className="donut-page">
        <div className="donut-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading submissions data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="donut-page">
        <div className="donut-container">
          <div className="error-state">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={fetchSubmissionsData} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalSubmissions = submissionsData.reduce((sum, item) => sum + item.count, 0);
  const submittedCount = submissionsData.find(item => item.status === 'Yes')?.count || 0;
  const notSubmittedCount = submissionsData.find(item => item.status === 'No')?.count || 0;
  const submissionRate = totalSubmissions > 0 ? ((submittedCount / totalSubmissions) * 100).toFixed(1) : 0;

  return (
    <div className="donut-page">
      <div className="donut-container">
        <div className="chart-wrapper">
          <div className="chart-container">
            <Doughnut data={chartData} options={options} />
          </div>
          
          {/* Side Statistics */}
          <div className="side-stats">
            <div className="side-stat">
              <h3>Total Projects</h3>
              <p className="side-number">{totalSubmissions.toLocaleString()}</p>
            </div>
            <div className="side-stat">
              <h3>Submission Rate</h3>
              <p className="side-number">{submissionRate}%</p>
            </div>
          </div>
        </div>
        
        {/* Summary Statistics */}
        <div className="chart-summary">
          <div className="summary-card submitted">
            <div className="card-icon">✅</div>
            <h3>Submitted</h3>
            <p className="summary-number">
              {submittedCount.toLocaleString()}
            </p>
            <p className="summary-percentage">
              {totalSubmissions > 0 ? ((submittedCount / totalSubmissions) * 100).toFixed(1) : 0}%
            </p>
          </div>
          <div className="summary-card not-submitted">
            <div className="card-icon">❌</div>
            <h3>Not Submitted</h3>
            <p className="summary-number">
              {notSubmittedCount.toLocaleString()}
            </p>
            <p className="summary-percentage">
              {totalSubmissions > 0 ? ((notSubmittedCount / totalSubmissions) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonutChartPage;
